import React, { FC, useEffect, useState } from "react";
import { Button } from "react-bootstrap";
import { UseFormReturn, useFieldArray } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { FormInput } from "go-form";
import { ApiError } from "go-core/api/types";
import { useWindowSize } from "go-core/components/useWindowSize";
import { ReactComponent as RemoveSVG } from "go-core/images/svg/actions/remove.svg";
import { FormSelectGroup } from "go-form/components/FormSelect";
import { ProductApi, ProductCustomIdApi } from "../../../../../../../../services/Api/Organization/types";
import GenerateProductBarcodeModalForm from "./GenerateProductBarcodeModalForm";

interface Props {
	form: UseFormReturn<ProductApi>;
	formErrors: ApiError[];
}

const filterCustomIdsErrors = (formErrors: ApiError[]) => {
	return formErrors.filter((error) => error.field?.includes("custom_ids"));
};

const ProductBarcodesForm: FC<Props> = ({ form, formErrors }) => {
	const {
		watch,
		control,
		register,
		setValue,
		formState: { errors },
	} = form;
	const { t } = useTranslation();
	const [showGenerateBarcodeModal, setShowGenerateBarcodeModal] = useState<number>(-1);
	const { fields, remove, append } = useFieldArray({
		control,
		name: "custom_ids",
		keyName: "key",
	});
	const watchedCustomIds = watch("custom_ids");
	const customIdsAsString = JSON.stringify(watchedCustomIds);
	const [focusedBarcodeValueInputIndex, setFocusedBarcodeValueInputIndex] = useState<number>(-1);
	const [customIdsErrors, setCustomIdsErrors] = useState(filterCustomIdsErrors(formErrors));
	const { isMobile, isSmallBreakpoint } = useWindowSize();

	useEffect(() => {
		setCustomIdsErrors(filterCustomIdsErrors(formErrors));
	}, [formErrors]);

	useEffect(() => {
		if (focusedBarcodeValueInputIndex !== -1) focusInput(focusedBarcodeValueInputIndex);
	}, [focusedBarcodeValueInputIndex]);

	const checkIfAddNewCustomId = () => {
		const prevCustomId = watchedCustomIds[watchedCustomIds?.length - 1];
		return prevCustomId?.value || prevCustomId?.type;
	};

	useEffect(() => {
		if (checkIfAddNewCustomId()) {
			append({
				id: new Date().getTime(),
				value: "",
				type: "",
			} as ProductCustomIdApi);
		}
	}, [customIdsAsString]);

	const getTypeOptions = () => [{ value: "BARCODE", label: "BARCODE" }];

	const onRemove = (index: number) => remove(index);

	const onCreateType = (value: string, index: number) => {
		setValue(`custom_ids.${index}.type`, value, { shouldDirty: true });
	};

	const onChangeType = (value: string, index: number) => {
		setValue(`custom_ids.${index}.type`, value);
	};

	const focusInput = (index: number) => {
		const input = document.getElementById(`custom_ids.${index}.value`);
		if (input) {
			input.focus();
		}
	};

	const handlePreventEnter = (e: React.KeyboardEvent<HTMLInputElement>) => {
		if (e.key === "Enter") {
			e.preventDefault();
			e.stopPropagation();
		}
	};

	const checkIfCustomIdHasError = (index: number) =>
		customIdsErrors.some((error) => error?.field?.includes(`[${index}].value`));

	return (
		<fieldset className={`form-group ${isSmallBreakpoint ? "table-responsive" : ""}`}>
			<table className="table table-form positions-table vertical-top">
				<thead>
					<tr>
						<th className="action" />
						<th style={{ minWidth: isMobile ? "150px" : "unset" }}>{t("lib:common.word.type")}</th>
						<th style={{ minWidth: isMobile ? "250px" : "unset" }}>{t("common.word.value")}</th>
						<th />
						<th className="action" />
					</tr>
				</thead>
				<tbody>
					{watchedCustomIds?.map((item, index) => (
						<tr key={fields[index].key}>
							<td className="action">
								<FormInput
									type="hidden"
									errors={errors}
									register={register}
									name={`custom_ids.${index}.id`}
									defaultValue={item.id}
								/>
							</td>
							<td>
								<FormSelectGroup
									errors={errors}
									control={control}
									name={`custom_ids.${index}.type`}
									onChange={(value) => onChangeType(value, index)}
									onMenuOpen={async () => getTypeOptions()}
									defaultValue={{
										value: watch(`custom_ids.${index}.type`),
										label: watch(`custom_ids.${index}.type`),
									}}
									getOptionValue={(opt) => opt.value}
									onCreateOption={(value) => onCreateType(value, index)}
									loadOptions={getTypeOptions}
									data-testid={`custom_ids.${index}.type`}
								/>
							</td>
							<td>
								<FormInput
									name={`custom_ids.${index}.value`}
									id={`custom_ids.${index}.value`}
									errors={errors}
									register={register}
									placeholder={t("modules.product.field.custom_id.placeholder")}
									onKeyDown={(event: React.KeyboardEvent<HTMLInputElement>) =>
										handlePreventEnter(event)
									}
								/>
								{checkIfCustomIdHasError(index) && (
									<div className="invalid-feedback ms-0" style={{ display: "inline" }}>
										{t("constraints.productCustomIdExist")}
									</div>
								)}
							</td>
							<td className="text-end w-25 no-border-focus">
								<div className="d-flex align-items-center justify-content-end">
									<Button className="me-2" onClick={() => setShowGenerateBarcodeModal(index)}>
										{t("modules.product.action.generate.title")}
									</Button>
									<span className="text-muted">{t("lib:common.word.or")}</span>
									<Button className="ms-2" onClick={() => setFocusedBarcodeValueInputIndex(index)}>
										{t("modules.product.action.scan_barcode.title")}
									</Button>
								</div>
							</td>
							<td className="action">
								{!(index === 0 && index === watchedCustomIds.length - 1) && (
									<RemoveSVG className="icon" onClick={() => onRemove(index)} />
								)}
							</td>
						</tr>
					))}
				</tbody>
			</table>
			<GenerateProductBarcodeModalForm
				isShown={showGenerateBarcodeModal > -1}
				key={`generate-barcode-modal-${showGenerateBarcodeModal}`}
				index={showGenerateBarcodeModal}
				form={form}
				onHide={() => setShowGenerateBarcodeModal(-1)}
			/>
		</fieldset>
	);
};

export default ProductBarcodesForm;
